import { CheckCircleFilled } from '@ant-design/icons'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { formatTimeWindow, normalizeDispatchDate } from '@components/_SiteWide/OrderDisplay/OrderTimeFormats'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { humanReadableSecondDifferenceFromToday } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import { ActionCell } from './components/cellRenderers/ActionCell'
import { Products } from './components/cellRenderers/Products'
import { RequestNumber } from './components/cellRenderers/RequestNumber'
import { Status } from './components/cellRenderers/Status'
import { Updated } from './components/cellRenderers/Updated'
import useLoadingWindowFilter from '@pages/OrderManagement/components/Grid/Filters/LoadingWindow'

export const getColumnDefs = (handleCancel, setVisible, setSelectedRequest, editRequestScope) => [
  {
    headerName: 'Request No.',
    field: 'number',
    cellRenderer: ({ data }) => <RequestNumber data={data} />,
  },
  {
    headerName: 'Site',
    field: 'site_name',
    comparator: (a, b) => a - b,
  },
  {
    headerName: 'Order Status',
    field: 'status',
    valueGetter: ({ data }) => (data.status === 'Pending' ? 'Received' : data.status),
    cellRenderer: ({ data, value }) => <Status data={data} status={value} />,
  },
  {
    headerName: 'ETA',
    field: 'eta',
    filter: 'agDateColumnFilter',
    filterValueGetter: ({ data }) => data.eta,
    ...defaultDateDef('eta', undefined, false),
    cellRenderer: ({ data }) => {
      if (data.eta === null) return 'N/A'
      const isPast = moment(data.eta).isBefore(moment())
      const eta = isPast ? formatters.DAY_TIME(data.eta) : normalizeDispatchDate(data.eta, true)
      if (data.status === 'Delivered') {
        return (
          <Horizontal verticalCenter>
            <CheckCircleFilled style={{ color: 'var(--theme-success)' }} className={'mr-1'} />
            {eta}
          </Horizontal>
        )
      }
      return eta
    },
  },
  {
    headerName: 'Customer',
    field: 'customer_name',
    cellRenderer: ({ data }) => <Texto className={'text-truncate'}> {data.customer_name}</Texto>,
  },
  {
    headerName: 'Sector',
    field: 'sector_name',
    initialHide: true,
  },
  { headerName: 'Market', field: 'market_name', initialHide: true },
  {
    headerName: 'Delivery Window',
    field: 'delivery_window_start',
    filter: useLoadingWindowFilter,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: ({ data }) => data.delivery_window_start,
    cellRenderer: ({ data }) => {
      if (!data.delivery_window_start) return 'N/A'
      if (moment(data.delivery_window_start).isBefore(moment())) {
        return (
          <Texto>
            {formatters.DAY_TIME(data.delivery_window_start)} - {formatters.DAY_TIME(data.delivery_window_end)}
          </Texto>
        )
      }
      return formatTimeWindow(data.delivery_window_start, data.delivery_window_end, true)
    },
    ...defaultDateDef('delivery_window_start', undefined, false),
  },
  {
    headerName: 'Time Left',
    field: 'time_left',
    filterValueGetter: ({ data }) => data.time_left && humanReadableSecondDifferenceFromToday(data.time_left),
    filter: 'agSetColumnFilter',
    cellRenderer: ({ data }) => (data.time_left ? humanReadableSecondDifferenceFromToday(data.time_left) : null),
    comparator: (a, b) => a - b,
  },
  {
    headerName: 'Total Volume',
    field: 'total_volume',
    ...defaultNumberDef('total_volume', 0),
    valueGetter: (params) => params?.data?.total_volume,
    comparator: (a, b) => {
      return a - b
    },
  },
  {
    headerName: 'Products',
    field: 'products',
    autoHeight: true,
    cellRenderer: ({ data }) => <Products data={data} />,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params?.data?.products?.map((product) => product.product_name),
    enableRowGroup: true,
  },
  {
    headerName: 'Updated Date',
    field: 'updated_date',
    cellRenderer: ({ data }) => <Updated data={data} />,
    ...defaultDateDef('updated_date', undefined, false),
    minWidth: 100,
  },

  {
    headerName: 'Actions',
    initialPinned: 'right',
    field: 'status',
    hide: !editRequestScope,
    cellRenderer: ({ data }) => {
      if (data.status === 'Pending' && editRequestScope)
        return (
          <ActionCell
            data={data}
            handleCancel={handleCancel}
            setVisible={setVisible}
            setSelectedRequest={setSelectedRequest}
            editRequestScope={editRequestScope}
          />
        )
    },
  },
]
